/**
 * Which projects are already orders, and so can no longer serve as the customer's cart.
 * Pure — no Vue, no Firebase.
 *
 * WHY: a placed order's cart contents are locked for non-staff (F-119, formfactories-functions
 * PR #745). The Firestore rules refuse link-holder writes to `addons` / `models` / `modelData` /
 * `itemOrder`, and the `addProductToProject` callable refuses non-staff adds with
 * `failed-precondition`. A project can become an order without ever getting a `checkout`:
 * `submit-draft-project` stamps `invoiceDate` on products-only projects, and staff invoice
 * through Zoho / QuickBooks. If such a project stayed the cart, every add-to-cart would fail.
 *
 * This is the UNION of the two server checks, so the cart is replaced whenever either one would
 * refuse an add:
 *  - firestore.rules isPlacedOrder: checkout, inventoryDeducted, an invoice field, amountPaid > 0,
 *    non-empty confirmedPayments
 *  - functions/storeAddonGuard.ts isOrder: the same minus inventoryDeducted, plus a collected
 *    payment in the raw Stripe `payments` array (amount_received + amount_capturable > 0)
 *
 * Vue 3 analog: cartProject.ts isUsableCartProject (stricter there: editable drafts only).
 */

// Order-only fields (cf. duplicateProject's "fields unique to an order" list).
const INVOICE_FIELDS = [
  'invoice',
  'invoiceDate',
  'zohoInvoice',
  'zohoInvoiceID',
  'quickbooksInvoice',
  'quickbooksInvoiceID',
]

const present = v => v !== undefined && v !== null && v !== ''

// `payments` holds raw PaymentIntents in any status; only money received or held counts.
function hasCollectedPayment (payments) {
  if (!Array.isArray(payments)) return false
  return payments.some(p => {
    if (!p || typeof p !== 'object') return false
    const received = typeof p.amount_received === 'number' ? p.amount_received : 0
    const capturable = typeof p.amount_capturable === 'number' ? p.amount_capturable : 0
    return received + capturable > 0
  })
}

/**
 * @param {object|null|undefined} project project document data
 * @returns {boolean} true once the project is checked out, invoiced or paid
 */
export function isPlacedOrder (project) {
  if (!project) return false
  if (present(project.checkout)) return true
  if (project.inventoryDeducted === true) return true
  if (INVOICE_FIELDS.some(k => present(project[k]))) return true
  if (typeof project.amountPaid === 'number' && project.amountPaid > 0) return true
  if (Array.isArray(project.confirmedPayments) && project.confirmedPayments.length > 0) return true
  return hasCollectedPayment(project.payments)
}

/**
 * Whether a project can (still) be the customer's cart. Keeps the legacy status rule from
 * validateProjectPreview (status 0 and 3 are never a cart) and adds the placed-order lock.
 *
 * @param {object|null|undefined} project project document data
 * @returns {boolean}
 */
export function isUsableCartProject (project) {
  if (!project) return false
  if (project.status === 0 || project.status === 3) return false
  return !isPlacedOrder(project)
}
